import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';

import { Command, MenuListProps } from './types';
import { DropdownButton } from '../../components/ui/Dropdown';
import Heading1 from 'external/TipTapEditor/components/SVG/Heading1';
import Heading2 from 'external/TipTapEditor/components/SVG/Heading2';
import Heading3 from 'external/TipTapEditor/components/SVG/Heading3';
import ListUnordered from 'external/TipTapEditor/components/SVG/ListUnordered';
import ListOrdered from 'external/TipTapEditor/components/SVG/ListOrdered';
import CheckList from 'external/TipTapEditor/components/SVG/CheckList';
import CodeIcon from 'external/TipTapEditor/components/SVG/CodeIcon';
import DoubleQuotesIcon from 'external/TipTapEditor/components/SVG/DoubleQuotesIcon';
import TableIcon from 'external/TipTapEditor/components/SVG/TableIcon';
import ColumnsIcon from 'external/TipTapEditor/components/SVG/ColumnsIcon';
import PilcrowIcon from 'external/TipTapEditor/components/SVG/PilcrowIcon';

export const slashCommandTypeToIconMap: Record<string, JSX.Element> = {
  Pilcrow: <PilcrowIcon />,
  Heading1: <Heading1 />,
  Heading2: <Heading2 />,
  Heading3: <Heading3 />,
  List: <ListUnordered />,
  ListOrdered: <ListOrdered />,
  ListTodo: <CheckList />,
  Quote: <DoubleQuotesIcon />,
  SquareCode: <CodeIcon />,
  Table: <TableIcon />,
  Columns2: <ColumnsIcon />,
};

export const MenuList = React.forwardRef((props: MenuListProps, ref) => {
  const scrollContainer = useRef<HTMLDivElement>(null);
  const activeItem = useRef<HTMLButtonElement>(null);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);

  // Anytime the groups change, i.e. the user types to narrow it down, we want to
  // reset the current selection to the first menu item
  useEffect(() => {
    setSelectedGroupIndex(0);
    setSelectedCommandIndex(0);
  }, [props.items]);

  const selectItem = useCallback(
    (groupIndex: number, commandIndex: number) => {
      const command = props.items[groupIndex].commands[commandIndex];
      props.command(command);
    },
    [props]
  );

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === 'ArrowDown') {
        if (!props.items.length) {
          return false;
        }

        const commands = props.items[selectedGroupIndex].commands;

        let newCommandIndex = selectedCommandIndex + 1;
        let newGroupIndex = selectedGroupIndex;

        if (commands.length - 1 < newCommandIndex) {
          newCommandIndex = 0;
          newGroupIndex = selectedGroupIndex + 1;
        }

        if (props.items.length - 1 < newGroupIndex) {
          newGroupIndex = 0;
        }

        setSelectedCommandIndex(newCommandIndex);
        setSelectedGroupIndex(newGroupIndex);

        return true;
      }

      if (event.key === 'ArrowUp') {
        if (!props.items.length) {
          return false;
        }

        let newCommandIndex = selectedCommandIndex - 1;
        let newGroupIndex = selectedGroupIndex;

        if (newCommandIndex < 0) {
          newGroupIndex = selectedGroupIndex - 1;
          newCommandIndex =
            props.items[newGroupIndex]?.commands.length - 1 || 0;
        }

        if (newGroupIndex < 0) {
          newGroupIndex = props.items.length - 1;
          newCommandIndex = props.items[newGroupIndex].commands.length - 1;
        }

        setSelectedCommandIndex(newCommandIndex);
        setSelectedGroupIndex(newGroupIndex);

        return true;
      }

      if (event.key === 'Enter') {
        if (
          !props.items.length ||
          selectedGroupIndex === -1 ||
          selectedCommandIndex === -1
        ) {
          return false;
        }

        selectItem(selectedGroupIndex, selectedCommandIndex);

        return true;
      }

      return false;
    },
  }));

  useEffect(() => {
    if (activeItem.current && scrollContainer.current) {
      const offsetTop = activeItem.current.offsetTop;
      const offsetHeight = activeItem.current.offsetHeight;

      scrollContainer.current.scrollTop = offsetTop - offsetHeight;
    }
  }, [selectedCommandIndex, selectedGroupIndex]);

  const createCommandClickHandler = useCallback(
    (groupIndex: number, commandIndex: number) => {
      return () => {
        selectItem(groupIndex, commandIndex);
      };
    },
    [selectItem]
  );

  if (!props.items.length) {
    return null;
  }

  return (
    <Paper
      ref={scrollContainer}
      elevation={1}
      sx={{
        maxHeight: 'min(80vh, 24rem)',
        overflow: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        marginBottom: 8,
        padding: 2,
        color: 'text.primary',
      }}
    >
      <Box width='100%'>
        {props.items.map((group, groupIndex: number) => (
          <React.Fragment key={`${group.title}-wrapper`}>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                mx: 1,
                mt: 4,
                userSelect: 'none',
                textTransform: 'uppercase',
                '&:first-of-type': {
                  marginTop: 0.5,
                },
              }}
              key={`${group.title}`}
            >
              {group.title}
            </Typography>
            {group.commands.map((command: Command, commandIndex: number) => (
              <DropdownButton
                key={`${command.label}`}
                ref={
                  selectedGroupIndex === groupIndex &&
                  selectedCommandIndex === commandIndex
                    ? activeItem
                    : null
                }
                isActive={
                  selectedGroupIndex === groupIndex &&
                  selectedCommandIndex === commandIndex
                }
                onClick={createCommandClickHandler(groupIndex, commandIndex)}
              >
                {slashCommandTypeToIconMap[command.iconName]}
                {command.label}
              </DropdownButton>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
});

MenuList.displayName = 'MenuList';

export default MenuList;
